import { apiJson } from '../utils/api.js';
import { handleApiError } from '../utils/errorHandler.js';

/**
 * Fetch all promocodes with optional filters and pagination
 * @param {Object} params - Search parameters
 * @param {string} params.type - Filter by promocode type (optional)
 * @param {boolean} params.isActive - Filter by active status (optional)
 * @param {string} params.userPhoneNumber - Filter by user phone number (optional)
 * @param {number} params.page - Page number for pagination (optional)
 * @param {number} params.size - Items per page (optional)
 * @returns {Promise<{promoCodeDtoList: Array, count: number}>}
 */
export const fetchPromoCodes = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.type) queryParams.append('type', params.type);
        if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
        if (params.userPhoneNumber) queryParams.append('userPhoneNumber', params.userPhoneNumber);
        
        // Backend uses offset/limit, not page/size
        // Convert page/size to offset/limit if provided
        if (params.page !== undefined && params.size !== undefined) {
            queryParams.append('offset', (params.page - 1) * params.size);
            queryParams.append('limit', params.size);
        } else {
            // Support direct offset/limit as well
            if (params.offset !== undefined) queryParams.append('offset', params.offset);
            if (params.limit !== undefined) queryParams.append('limit', params.limit);
        }

        const response = await apiJson.get(`/kms/promo-code?${queryParams.toString()}`);
        return response.data;
    } catch (error) {
        // Only show toast error for non-404 errors
        if (error.response?.status !== 404) {
            handleApiError(error, {
                defaultMessage: "Error: 4001: Պրոմո կոդերը չեն գտնվել: Խնդրում ենք ստուգել տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին:"
            });
        }
        throw error;
    }
};

/**
 * Fetch a single promocode by ID
 * @param {string|number} id - Promocode ID
 * @returns {Promise<Object>}
 */
export const fetchPromoCodeById = async (id) => {
    try {
        const response = await apiJson.get(`/kms/promo-code/${id}`);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 4002: Պրոմո կոդը չի գտնվել: Խնդրում ենք ստուգել տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};

/**
 * Create a new promocode
 * @param {Object} promoCode - The promocode data
 * @param {string} promoCode.code - Unique promocode
 * @param {string} promoCode.type - Type of promocode (FIXED, PERCENTAGE, etc.)
 * @param {number} promoCode.value - Value of the promocode
 * @param {string} promoCode.sponsor - Sponsor type
 * @param {number} promoCode.splitRatio - Split ratio for the promocode
 * @param {number} promoCode.minimumOrderAmount - Minimum order amount
 * @param {Array<string>} promoCode.allowedPaymentTypes - Allowed payment types
 * @param {number} promoCode.maxUses - Maximum total uses
 * @param {number} promoCode.maxUsesPerUser - Maximum uses per user
 * @param {string} promoCode.validFrom - Start date (YYYY-MM-DD)
 * @param {string} promoCode.validTo - End date (YYYY-MM-DD)
 * @param {boolean} promoCode.isActive - Whether the promocode is active
 * @returns {Promise<Object>}
 */
export const createPromoCode = async (promoCode) => {
    try {
        const response = await apiJson.post('/kms/promo-code', promoCode);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 4003: Պրոմո կոդը չի ստեղծվել: Խնդրում ենք ստուգել տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};

/**
 * Update an existing promocode
 * @param {string|number} id - Promocode ID
 * @param {Object} promoCode - Updated promocode data
 * @returns {Promise<Object>}
 */
export const updatePromoCode = async (id, promoCode) => {
    try {
        const response = await apiJson.put(`/kms/promo-code/${id}`, promoCode);
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 4004: Պրոմո կոդը չի թարմացվել: Խնդրում ենք ստուգել տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};

/**
 * Delete a promocode
 * @param {string|number} id - Promocode ID
 * @returns {Promise<void>}
 */
export const deletePromoCode = async (id) => {
    try {
        await apiJson.delete(`/kms/promo-code/${id}`);
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 4005: Պրոմո կոդը չի ջնջվել: Խնդրում ենք ստուգել տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};

/**
 * Toggle promocode active status
 * @param {string|number} id - Promocode ID
 * @param {boolean} isActive - New active status
 * @returns {Promise<Object>}
 */
export const togglePromoCodeStatus = async (id, isActive) => {
    try {
        const response = await apiJson.put(`/kms/promo-code/${id}`, { isActive });
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 4006: Պրոմո կոդի կարգավիճակը չի փոխվել: Խնդրում ենք ստուգել տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժին:"
        });
        throw error;
    }
};
